<template>
    <VJoyDialog data-testid="vetting-reason-dialog" aria-labelledby="vettingConfirmDialog" open @dialog:hide="emits('cancel')">
        <template #dialog-header>
            <p id="confirmDialogTitle">
                {{ $t(`profile.vetting.community.vetting.decision.dialog.title.${action.toUpperCase()}.${vertical}` as TranslationKey) }}
            </p>
        </template>

        <template #dialog-body>
            <form :id="formId">
                <SuggestionWarning class="warning" :warnings="warnings" />
                <!-- eslint-disable-next-line vue/no-v-html -->
                <p v-html="$t(`profile.vetting.community.vetting.decision.dialog.subtitle.${action.toUpperCase()}.${vertical}` as TranslationKey)" />
                <VJoyTextarea
                    v-model="r$.$value.reason"
                    :invalid="r$.reason.$error"
                    :placeholder="$t(`profile.vetting.community.vetting.decision.dialog.reason.placeholder`)"
                    data-testid="vetting-reason-dialog-input-reason"
                />
                <VJoyFormError v-if="r$.reason.$error" class="joy-mt-4" :errors="r$.reason.$errors" />
                <p class="joy-font-size-primary-300 joy-mt-4">
                    {{ $t(`profile.vetting.community.vetting.decision.dialog.confirm`) }}
                </p>
            </form>
        </template>
        <template #dialog-cancel>
            <VJoyButton
                variant="ghost"
                :loading="!!freelancerVerticalStore.loading"
                :label="$t('profile.vetting.community.vetting.decision.dialog.cancel.cta')"
                @click="emits('cancel')"
            />
        </template>
        <template #dialog-confirm>
            <VJoySubmit
                data-testid="vetting-reason-dialog-submit"
                :form="formId"
                variant="main"
                :loading="!!freelancerVerticalStore.loading"
                :action="handleConfirm"
            >
                {{ $t('profile.vetting.community.vetting.decision.dialog.confirm.cta') }}
            </VJoySubmit>
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import {required} from '@regle/rules';
    import {VJoySubmit} from '#components';
    import {useRegle, validateForm} from '@malt/forms';
    import {pushVJoySnackbar, VJoyButton, VJoyDialog, VJoyFormError, VJoyTextarea} from '@maltjoy/core-vue';
    import {FreelancerVertical} from '@api';
    import SuggestionWarning from '~/components/admin-section-tabs/community/SuggestionWarning.vue';
    import {useFreelancerVerticalStore} from '~/store/freelancerVerticalStore';
    import {DialogAction} from './Dialog.model';
    import type {TranslationKey} from '#build/i18n';

    const freelancerVerticalStore = useFreelancerVerticalStore();
    const formId = 'vetting-reason-dialog-form';

    const {r$} = useRegle(
        {reason: ''},
        {
            reason: {
                required,
            },
        },
    );

    const props = defineProps<{
        vertical: FreelancerVertical;
        action: DialogAction;
        warnings: string[];
    }>();

    const emits = defineEmits<{
        confirm: [];
        cancel: [];
    }>();

    async function handleConfirm() {
        const {valid, data} = await validateForm(r$, {formId});
        if (!valid) {
            return;
        }
        try {
            if (props.action === DialogAction.Apply && props.vertical === FreelancerVertical.Strategy) {
                await freelancerVerticalStore.suggestToStrategy(data.reason);
                emits('confirm');
            } else if (props.action === DialogAction.Accept && props.vertical === FreelancerVertical.Regular) {
                await freelancerVerticalStore.acceptToRegular(data.reason);
                emits('confirm');
            }
        } catch (error: unknown) {
            pushVJoySnackbar({
                props: {
                    message: error instanceof Error ? error.message : String(error),
                    level: 'error',
                },
            });
            throw error;
        }
    }
</script>

<style lang="scss" scoped>
    .warning {
        margin-bottom: 20px;
    }
</style>
